"use client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import * as yup from "yup";
import { BsGoogle } from "react-icons/bs";
import {
  AKREDITASI_UI_URL,
  KLC_UI_URL,
  RECAPTCHA_SITE_KEY,
} from "@/lib/constant";
import { IoArrowBack } from "react-icons/io5";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { signIn } from "next-auth/react";
import Footer from "@/components/dashboard/Footer/Footer";
import { yupResolver } from "@hookform/resolvers/yup";
import { set, useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { toast, ToastContainer } from "react-toastify";
import { useRouter } from "next/navigation";

const validationSchema = yup.object({
  username: yup.string().required("Email diperlukan"),
  password: yup.string().required("Kata Sandi diperlukan"),
});

const LoginPage = () => {
  const [page, setPage] = useState("default");
  const [checkcaptcha, setCheckcaptcha] = useState(false);
  const router = useRouter();

  const handleCaptchaResponseChange = (response) => {
    setCheckcaptcha(true);
  };

  const handleSignInWithKemenkeu = () => {
    signIn("keycloak-kemenkeu", {
      callbackUrl: `${AKREDITASI_UI_URL}`,
    });
  };

  const handleSignInWithGoogle = () => {
    signIn("keycloak-google", {
      callbackUrl: `${AKREDITASI_UI_URL}`,
    });
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleLogin = async ({ username, password }) => {
    if (!checkcaptcha) {
      toast.error("Please complete the CAPTCHA verification.");
      return;
    }

    const result = await signIn("credentials", {
      redirect: false, // Prevents auto-redirection
      username,
      password,
      checkcaptcha: true,
      realm: "eksternal-dev",
      callbackUrl: "/akreditasi", // Default redirection if login succeeds
    });

    if (result?.error) {
      toast.error("Invalid username or password. Please try again."); // Show error toast
      setCheckcaptcha(false); // Reset captcha verification
      router.push("/login"); // Redirect to login page
    } else {
      router.push(result.url || "/akreditasi"); // Successful login redirect
    }
  };

  return (
    <div className="bg-[#f5f5f5] h-full">
      <ToastContainer position="top-center" autoClose={3000} />
      <section className="grid p-8 sm:grid-cols-2 ">
        <div>
          <Image
            alt=""
            src={"https://klc2.kemenkeu.go.id/shared/assets/images/login.svg"}
            width={100}
            height={100}
            className="mb-10 w-max"
          />
        </div>
        {page === "default" ? (
          <div className="flex flex-col items-center w-full mx-auto md:h-screen lg:py-0 gap-y-2">
            <Card className=" flex w-full h-[60vh] items-center  text-center bg-white rounded-lg shadow i text-customDarkBlue md:mt-0 sm:max-w-md xl:p-0 ">
              <div className="items-center justify-center p-6 space-y-4 md:space-y-6 sm:p-8">
                <h1 className="font-bold leading-tight tracking-tight text-center text-md md:text-xl">
                  Selamat Datang di KLC Akreditasi <br />
                  <span className="text-sm font-normal">
                    Masuk untuk mengeksplorasi galaksi pengetahuan!
                  </span>
                </h1>
                <p className="my-2 text-md">Darimana Anda Berasal?</p>

                <Button
                  className="flex items-center justify-center w-full px-4 py-6 bg-customDarkBlue"
                  onClick={handleSignInWithKemenkeu}
                >
                  <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">
                    Kementerian Keuangan
                  </div>
                </Button>

                <Button
                  className="flex items-center justify-center w-full px-4 py-6 my-4 border-2 shadow-lg border-customDarkBlue"
                  onClick={() => setPage("notKementrian")}
                  variant="outline"
                >
                  <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">
                    Selain Kementerian Keuangan
                  </div>
                </Button>

                <p className="text-sm">
                  Gunakan akun Anda secara bijak <br /> dan{" "}
                  <span className="font-bold">JANGAN</span> bagi pakai akun Anda
                  kepada orang lain.
                </p>
              </div>
            </Card>
          </div>
        ) : (
          <div className="flex flex-col items-center w-full mx-auto mb-20 md:h-screen lg:py-0 gap-y-6">
            <Card className="items-center w-full text-center bg-white rounded-lg shadow text-customDarkBlue md:mt-0 sm:max-w-md xl:p-0">
              <div className="items-center justify-center p-6 space-y-4 md:space-y-6 sm:p-8">
                <div
                  className="flex text-sm cursor-pointer w-fit gap-x-2 opacity-60 "
                  onClick={() => setPage("default")}
                >
                  <IoArrowBack className="self-center" />
                  Kembali
                </div>
                <hr />
                <Button
                  className="flex items-center justify-center w-full px-4 py-6 bg-[#DB4437] hover:bg-[#9b4038]"
                  onClick={handleSignInWithGoogle}
                >
                  <div className="flex overflow-hidden whitespace-nowrap overflow-ellipsis gap-x-2">
                    <BsGoogle className="self-center" /> Masuk dengan Google
                  </div>
                </Button>
                <div className="flex items-center justify-center my-4">
                  <hr className="flex-grow border-gray-300" />
                  <span className="px-2 text-sm text-gray-500">Atau</span>
                  <hr className="flex-grow border-gray-300" />
                </div>
                <h1 className="font-bold leading-tight tracking-tight text-center text-md md:text-xl">
                  <span className="text-sm font-normal">
                    Untuk selain pengguna Google
                  </span>
                </h1>
                <form
                  className="text-left"
                  onSubmit={handleSubmit((data) =>
                    handleLogin({ ...data, checkcaptcha: true })
                  )}
                >
                  <Label className="mb-1">Username</Label>
                  <Input
                    type="text"
                    name="username"
                    {...register("username")}
                  />
                  <Label className="mb-1">Kata Sandi</Label>
                  <Input
                    type="password"
                    name="password"
                    {...register("password")}
                  />
                  <div className="mt-4">
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleCaptchaResponseChange}
                      size="normal"
                      className="test "
                    />
                  </div>

                  <Button className="flex items-center justify-center w-full px-4 py-6 my-4 border-2 shadow-lg bg-customGreen">
                    <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">
                      Masuk
                    </div>
                  </Button>
                </form>
                <div className="flex justify-end w-full underline">
                  <Link href={"/akreditasi"}>Lupa kata sandi?</Link>
                </div>
              </div>
            </Card>
            <Card className=" flex w-full h-[20vh] items-center  text-center bg-white rounded-lg shadow i text-customDarkBlue mb-10 md:mt-0 sm:max-w-md xl:p-0 ">
              <div className="flex flex-col items-center justify-center w-full p-6 sm:p-8">
                <p>Belum punya Akun KLC?</p>
                <Link href={`${KLC_UI_URL}/signup`}>
                  <Button className="flex items-center justify-center w-full px-4 py-6 my-4 bg-customDarkBlue">
                    <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">
                      Daftar
                    </div>
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default LoginPage;
